import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";
import Layout from "./components/Layout";
import AdminLayout from "./components/AdminLayout";
import ProtectedRoute from "./components/ProtectedRoute";
import BackendUrlBanner from "./components/BackendUrlBanner";
import Home from "./pages/Home";
import RootGate from "./components/RootGate";
import Vehicles from "./pages/Vehicles";
import VehicleDetail from "./pages/VehicleDetail";
import Booking from "./pages/Booking";
import BookingSuccess from "./pages/BookingSuccess";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import AdminDashboard from "./pages/admin/Dashboard";
import AdminBookings from "./pages/admin/Bookings";
import AdminFleet from "./pages/admin/Fleet";
import AdminBusiness from "./pages/admin/Business";
import AdminLocations from "./pages/admin/Locations";
import AdminUsers from "./pages/admin/Users";
import AdminCalendar from "./pages/admin/Calendar";
import AdminVouchers from "./pages/admin/Vouchers";
import AdminCarFleet from "./pages/admin/CarFleet";
import AdminRentalSettings from "./pages/admin/RentalSettings";
import AdminSystemSettings from "./pages/admin/SystemSettings";
import AdminCarExtras from "./pages/admin/CarExtras";
import AdminInquiries from "./pages/admin/Inquiries";
import AdminPriceRadar from "./pages/admin/PriceRadar";
import AdminFleetRadar from "./pages/admin/FleetRadar";
import AdminReports from "./pages/admin/Reports";
import AdminClients from "./pages/admin/Clients";
import PublicBook from "./pages/PublicBook";
import ManageBooking from "./pages/ManageBooking";
import MokeDemo from "./pages/MokeDemo";

const STAFF_ROLES = ["admin", "super_admin", "staff", "editor"];

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <BackendUrlBanner />
          <Routes>
            {/* Root is a gate — anon users bounce to /login, authed users to their console.
                The public marketing Home stays available at /preview/home behind auth
                so admins can still preview the customer landing UX. */}
            <Route path="/" element={<RootGate />} />
            {/* Fully public booking landing — NO admin/customer chrome, NO auth. */}
            <Route path="/book" element={<PublicBook />} />
            {/* Public self-service portal (v3.9.6). */}
            <Route path="/manage-booking" element={<ManageBooking />} />
            {/* Isolated lifestyle demo funnel (v4.0.4) — MOKE Experience. */}
            <Route path="/demo/moke" element={<MokeDemo />} />
            <Route path="/preview/home" element={<Layout><ProtectedRoute><Home /></ProtectedRoute></Layout>} />

            {/* Customer catalogue + booking — auth required (customers use this;
                admins access it as a Preview from the console). */}
            <Route path="/vehicles" element={<Layout><ProtectedRoute><Vehicles /></ProtectedRoute></Layout>} />
            <Route path="/vehicles/:id" element={<Layout><ProtectedRoute><VehicleDetail /></ProtectedRoute></Layout>} />
            <Route path="/booking/success" element={<Layout><ProtectedRoute><BookingSuccess /></ProtectedRoute></Layout>} />
            <Route path="/booking/:id" element={<Layout><ProtectedRoute><Booking /></ProtectedRoute></Layout>} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/dashboard" element={<Layout><ProtectedRoute><Dashboard /></ProtectedRoute></Layout>} />

            {/* Admin & Staff console */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute adminOnly>
                  <AdminLayout><AdminDashboard /></AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/bookings"
              element={
                <ProtectedRoute allowedRoles={STAFF_ROLES}>
                  <AdminLayout><AdminBookings /></AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/clients"
              element={
                <ProtectedRoute allowedRoles={STAFF_ROLES}>
                  <AdminLayout><AdminClients /></AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/fleet"
              element={
                <ProtectedRoute adminOnly>
                  <AdminLayout><AdminFleet /></AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/business"
              element={
                <ProtectedRoute adminOnly>
                  <AdminLayout><AdminBusiness /></AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/system-settings"
              element={
                <ProtectedRoute adminOnly>
                  <AdminLayout><AdminSystemSettings /></AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/locations"
              element={
                <ProtectedRoute adminOnly>
                  <AdminLayout><AdminLocations /></AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute adminOnly>
                  <AdminLayout><AdminUsers /></AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/price-radar"
              element={
                <ProtectedRoute allowedRoles={STAFF_ROLES}>
                  <AdminLayout><AdminPriceRadar /></AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/reports"
              element={
                <ProtectedRoute adminOnly>
                  <AdminLayout><AdminReports /></AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/fleet-radar"
              element={
                <ProtectedRoute allowedRoles={STAFF_ROLES}>
                  <AdminLayout><AdminFleetRadar /></AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/calendar"
              element={
                <ProtectedRoute allowedRoles={STAFF_ROLES}>
                  <AdminLayout><AdminCalendar /></AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/vouchers"
              element={
                <ProtectedRoute adminOnly>
                  <AdminLayout><AdminVouchers /></AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/car-fleet"
              element={
                <ProtectedRoute adminOnly>
                  <AdminLayout><AdminCarFleet /></AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/rental-settings"
              element={
                <ProtectedRoute adminOnly>
                  <AdminLayout><AdminRentalSettings /></AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/car-extras"
              element={
                <ProtectedRoute adminOnly>
                  <AdminLayout><AdminCarExtras /></AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/inquiries"
              element={
                <ProtectedRoute adminOnly>
                  <AdminLayout><AdminInquiries /></AdminLayout>
                </ProtectedRoute>
              }
            />
          </Routes>
          <Toaster position="top-center" richColors closeButton />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
